import { useState, useEffect } from 'react';
import { Event } from '@/types/event';
import { eventService } from '@/services/eventService';
import { useAuth } from './useAuth';
import { useInvitedEvents } from './useInvitedEvents';
import { supabase } from '@/integrations/supabase/client';

export const useEvents = () => {
  const { user } = useAuth();
  const [createdEvents, setCreatedEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Use the new invited events hook
  const { 
    invitedEvents, 
    loading: invitedLoading, 
    error: invitedError 
  } = useInvitedEvents();

  // Combined loading state
  const combinedLoading = loading || invitedLoading;
  
  // Combined error state
  const combinedError = error || invitedError;

  const loadCreatedEvents = async () => {
    if (!user) {
      console.log('⚠️ [useEvents] No user found, skipping created events load');
      setCreatedEvents([]);
      setLoading(false);
      return;
    }
    
    console.log('🚀 [useEvents] Loading created events for user:', {
      id: user.id,
      email: user.email,
      timestamp: new Date().toISOString()
    });
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch only events created by the current user
      const { data: ownedEvents, error: ownedError } = await supabase
        .from('events')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (ownedError) {
        console.error('❌ [useEvents] Error fetching owned events:', ownedError);
        throw new Error(`Failed to fetch created events: ${ownedError.message}`);
      }

      console.log('✅ [useEvents] Fetched owned events:', ownedEvents?.length || 0);

      if (!ownedEvents || ownedEvents.length === 0) {
        console.log('📭 [useEvents] No created events found');
        setCreatedEvents([]);
        setLoading(false);
        return;
      }

      // Get related data for created events
      const eventIds = ownedEvents.map(e => e.id);
      
      // Get options
      const { data: options, error: optionsError } = await supabase
        .from('event_options')
        .select('*')
        .in('event_id', eventIds);

      if (optionsError) {
        console.error('❌ [useEvents] Error fetching options:', optionsError);
      }

      // Get guests
      const { data: guests, error: guestsError } = await supabase
        .from('event_guests')
        .select('*')
        .in('event_id', eventIds);

      if (guestsError) {
        console.error('❌ [useEvents] Error fetching guests:', guestsError);
      }

      // Get votes
      const { data: votes, error: votesError } = await supabase
        .from('event_votes')
        .select('*')
        .in('event_id', eventIds);

      if (votesError) {
        console.error('❌ [useEvents] Error fetching votes:', votesError);
      }

      // Transform to Event objects
      const transformedEvents: Event[] = ownedEvents.map(event => {
        const eventOptions = options?.filter(opt => opt.event_id === event.id) || [];
        const eventGuests = guests?.filter(guest => guest.event_id === event.id) || [];
        const eventVotes = votes?.filter(vote => vote.event_id === event.id) || [];

        // Calculate vote counts for each option
        const voteCounts: Record<string, Record<string, number>> = {};
        eventOptions.forEach(option => {
          voteCounts[option.id] = {
            prefer: 0,
            unavailable: 0,
            accept: 0,
            decline: 0
          };
        });

        eventVotes.forEach(vote => {
          if (vote.option_id && voteCounts[vote.option_id]) {
            voteCounts[vote.option_id][vote.vote_type]++;
          }
        });

        // Mark guests as having voted if they have any votes
        const guestsWithVoteStatus = eventGuests.map(guest => ({
          ...guest,
          hasVoted: eventVotes.some(vote => vote.guest_id === guest.id)
        }));

        return {
          id: event.id,
          name: event.name,
          description: event.description,
          hostName: event.host_name,
          userId: event.user_id,
          eventType: event.event_type as 'voting' | 'rsvp',
          deadline: event.deadline,
          isActive: event.is_active,
          shareableLink: event.shareable_link,
          rsvpDate: event.rsvp_date,
          rsvpTime: event.rsvp_time,
          rsvpLocation: event.rsvp_location,
          familyGroupId: event.family_group_id,
          clientId: event.client_id,
          allowGuestSuggestions: event.allow_guest_suggestions,
          totalVoters: guestsWithVoteStatus.length,
          options: eventOptions.map(option => ({
            id: option.id,
            type: option.type as 'date' | 'time' | 'location' | 'rsvp',
            value: option.value,
            votes: Object.values(voteCounts[option.id] || {}).reduce((sum, count) => sum + count, 0),
            voters: eventVotes
              .filter(vote => vote.option_id === option.id)
              .map(vote => vote.guest_id),
            status: option.status as 'host' | 'pending' | 'approved' | 'rejected',
            suggestedByGuestId: option.suggested_by_guest_id,
            voteCount: voteCounts[option.id] || { prefer: 0, unavailable: 0, accept: 0, decline: 0 }
          })),
          guests: guestsWithVoteStatus.map(guest => ({
            id: guest.id,
            name: guest.name,
            email: guest.email,
            phone: guest.phone,
            hasVoted: guest.hasVoted,
            eventId: guest.event_id,
            invitedAt: guest.invited_at
          })),
          votes: eventVotes.map(vote => ({
            id: vote.id,
            guestId: vote.guest_id,
            optionId: vote.option_id,
            voteType: vote.vote_type as 'prefer' | 'unavailable' | 'accept' | 'decline',
            eventId: vote.event_id,
            createdAt: vote.created_at
          }))
        };
      });

      console.log('✅ [useEvents] Successfully transformed created events:', transformedEvents.length);
      setCreatedEvents(transformedEvents);
      
    } catch (err) {
      console.error('❌ [useEvents] Error loading created events:', err);
      setError(`Failed to load created events: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCreatedEvents();
  }, [user]);

  // Set up real-time subscription for created events
  useEffect(() => {
    if (!user) return;

    console.log('🔄 [useEvents] Setting up real-time subscription for created events');
    
    const channel = supabase
      .channel('user_created_events')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'events',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('🔔 [useEvents] Created events change detected:', payload);
          loadCreatedEvents(); // Reload when user's events change
        }
      )
      .subscribe();

    return () => {
      console.log('🧹 [useEvents] Cleaning up created events subscription');
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Combine all events for backward compatibility
  const allEvents = [...createdEvents, ...invitedEvents];

  const createEvent = async (eventData: any) => {
    try {
      console.log('Creating event:', eventData);
      const newEvent = await eventService.createEvent(eventData);
      console.log('Event created successfully:', newEvent);
      setCreatedEvents(prev => [newEvent, ...prev]);
      return newEvent;
    } catch (err) {
      console.error('Error creating event:', err);
      throw err;
    }
  };

  const updateEvent = async (eventId: string, updates: any) => {
    try {
      console.log('Updating event:', eventId, updates);
      const updatedEvent = await eventService.updateEvent(eventId, updates);
      
      // Update in created events if it exists there
      setCreatedEvents(prev => prev.map(event => 
        event.id === eventId ? updatedEvent : event
      ));
      
      return updatedEvent;
    } catch (err) {
      console.error('Error updating event:', err);
      throw err;
    }
  };

  const deleteEvent = async (eventId: string) => {
    try {
      console.log('Deleting event:', eventId);
      await eventService.deleteEvent(eventId);
      
      // Remove from created events
      setCreatedEvents(prev => prev.filter(event => event.id !== eventId));
    } catch (err) {
      console.error('Error deleting event:', err);
      throw err;
    }
  };

  return {
    events: allEvents, // For backward compatibility
    createdEvents,
    invitedEvents,
    loading: combinedLoading,
    error: combinedError,
    loadEvents: loadCreatedEvents, // For backward compatibility
    createEvent,
    updateEvent,
    deleteEvent,
  };
};